import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { ButtonModule } from '@progress/kendo-angular-buttons';
import { AuthService } from '@src/app/core/auth/services/auth.service';
import * as fromStore from '@src/app/store/conventional';

@Component({
  selector: 'eula',
  imports: [
    CommonModule,
    ButtonModule
  ],
  template: `
    <div class="h-dvh md:py-[60px]">
      <div class="mx-auto flex h-full max-w-[924px] flex-col gap-6 rounded p-4 md:bg-theme-foreground md:p-8 md:shadow-eula-container">
        <div class="grid gap-6">
          @if(name$ | async) {
            <p class="text-[22px] text-[#212529]">Hi {{ name$ | async }},</p>
          } @else {
            <p class="text-[22px] text-[#212529]">Welcome,</p>
          }
          <span class="text-[#919191]">Before continuing, please review our license terms:</span>
        </div>
        <div class="flex-1 overflow-y-auto rounded border border-[#D9D9D9] p-4">
              <h3>
                <strong>Cargo Signal: End User License Agreement</strong>
              </h3>
              <p>
                <strong>Agreement Date and Version:</strong>
              </p>
              <br>
              <p>DATE OF LAST REVISION: October 15, 2018</p>
              <br>
              <p>AGREEMENT VERSION NO.: 2.0</p>
              <br>
              <p>
                <strong>IMPORTANT! READ THIS ENTIRE AGREEMENT CAREFULLY.</strong>
              </p>
              <p>
                <strong>
                  PLEASE READ THIS AGREEMENT CAREFULLY AS IT CONTAINS IMPORTANT
                  <br />
                  INFORMATION REGARDING YOUR LEGAL RIGHTS, REMEDIES, AND OBLIGATIONS,
                  <br />
                  INCLUDING VARIOUS LIMITATIONS AND EXCLUSIONS.
                </strong>
              </p>
              <br>
              <p>
                Cargo Signal Solutions, LLC (&ldquo;Cargo Signal&rdquo; or &ldquo;<strong
                  ><u>we</u></strong
                >&rdquo;, &ldquo;<strong><u>us</u></strong
                >&rdquo;, or &ldquo;<strong><u>our</u></strong
                >&rdquo;) are pleased to give you (also referred to as &ldquo;I&rdquo;) access to the
                Cargo Signal System along with other related or linked websites, applications, services,
                interfaces, tools, software, and other functionality provided on or through the Cargo
                Signal System (collectively, the &ldquo;App&rdquo;) subject to your agreement to comply
                with the terms and conditions of this End User License Agreement (&ldquo;<strong
                  ><u>EULA</u></strong
                >&rdquo;), and any and all related Scope &amp; Terms for Service Agreements (if any)
                applying to the software or services provided pursuant to this EULA (the &ldquo;<strong
                  ><u>Additional Terms and </u></strong
                >
                <strong><u>Conditions</u></strong
                >&rdquo;) (collectively, the &ldquo;<strong><u>Agreement</u></strong
                >&rdquo;).
              </p>
              <br>
              <p>
                BY ACCESSING OR USING THE APP, OR BY CLICKING TO ACCEPT OR AGREE TO THIS AGREEMENT WHEN
                THAT OPTION IS MADE AVAILABLE TO YOU, YOU ACCEPT AND AGREE TO BE BOUND BY THIS AGREEMENT
                AS UPDATED FROM TIME TO TIME. IF YOU DO NOT AGREE TO ALL OF THE TERMS OF THIS AGREEMENT
                OR ARE UNWILLING TO ABIDE BY ANY TERM OR CONDITION, DO NOT ACCESS OR USE THE APP.
                ACCORDINGLY, YOUR ACCEPTANCE OF THIS AGREEMENT OR USE OF THE APP CONSTITUTES ACCEPTANCE
                OF THESE TERMS AND CONDITIONS AND GIVES RISE TO A BINDING LEGAL AGREEMENT BETWEEN CARGO
                SIGNAL AND YOU GOVERNING YOUR USE OF THE APP. BY ACCESSING OR USING THE APP, YOU HEREBY
                UNDERSTAND, ACKNOWLEDGE, REPRESENT, AND AGREE AS FOLLOWS:
              </p>
              <br>
              <ol class="list-decimal pl-4">
                <li>
                  <strong> I Understand that Cargo Signal May Change this Agreement.</strong>
                  <p>
                    Cargo Signal reserves the right, at its sole discretion, to change, modify, add, or
                    delete portions of this Agreement at any time and without any further notice to you.
                    You, however, may not amend this Agreement unless the amendment is in writing and
                    signed by both you and Cargo Signal.
                  </p>
                  <br>
                  <p>
                    It is your responsibility to regularly check for changes to this Agreement and to
                    review such changes. Accordingly, you understand that we may update this Agreement
                    from time to time without giving you any notice. If we do this, we will post the
                    new, modified version of this Agreement in the App. All changes to this Agreement
                    are effective immediately when we post them and apply to all access to and use of
                    the App thereafter. Any changes to the dispute resolution provisions, however, will
                    not apply to any dispute for which the parties have notice before the date the new
                    version of this Agreement is posted.
                  </p>
                  <br>
                  <p>
                    Your continued access or use of the App after any changes to this Agreement
                    constitutes your acceptance of the revised Agreement. Your written approval is not a
                    prerequisite to the validity or enforceability of this Agreement, and no
                    solicitation of such written approval by or on behalf of Cargo Signal shall be
                    construed as an inference to the contrary. In some cases, however, you may be asked
                    to agree to revised terms when you access the App and if you do not agree to the
                    revised terms you will not be allowed to access or use the App.
                  </p>
                </li>
              </ol>
              <br>
              <ol start="2" class="list-decimal pl-4">
                <li>
                  <strong> I Understand that this Agreement is Effective Until Terminated.</strong>
                  <p>
                    This Agreement is effective until terminated. Unless stated otherwise in the
                    Agreement, your rights under this Agreement will terminate automatically with 15
                    days&rsquo; notice from Cargo Signal if you fail to comply with any of the terms,
                    conditions, or guidelines of this Agreement. Provisions of this Agreement, however,
                    that require or contemplate performance after the termination of this Agreement
                    shall survive and be enforceable after the termination of this Agreement.
                  </p>
                  <p>
                    You may terminate this Agreement by providing written notice to Cargo Signal of your
                    decision to terminate and by ceasing to use the App. Your decision to terminate this
                    Agreement will be effective 30 days after we receive your written notice.
                  </p>
                  <br>
                  <p>
                    Upon the termination of this Agreement, you shall cease all use of the App. In the
                    event that your rights to use the App are terminated, you may immediately lose
                    access to any information that may be on our systems. We can terminate your access
                    to the App at any time. We may also remove any User Content (as defined below) that
                    you post without cause and without notice to you.
                  </p>
                </li>
              </ol>
              <br>
              <ol start="3" class="list-decimal pl-4">
                <li>
                  <strong> I Understand that Cargo Signal May Change the App.</strong>
                  <p>
                    Cargo Signal reserves the right, in its sole discretion, without notice to you, at
                    any time and from time to time, temporarily or permanently, in whole or in part, to:
                    (a) modify, suspend, terminate, withdraw, or discontinue all or any part of the App
                    without compensation to you, including, but not limited to (i) restricting the time
                    all or any part of the App is available, (ii) restricting the amount of permitted
                    use of all or any part of the App, and (iii) restricting or terminating any
                    user&rsquo;s right to use all or any part of the App, with or without notice; (b)
                    charge fees in connection with the use of all or any part of the App; (c) charge
                    fees for upgrades to the App; (d) modify or waive any fees charged in connection
                    with all or any part of the App; or (e) offer opportunities to some or all users of
                    all or any part of the App. Any new features or updated content or applications that
                    we make available as a part of the App will be subject to this Agreement. You agree
                    that Cargo Signal will not be liable to you or to any third party for any
                    modification, suspension, or discontinuance of all or any part of the App, in whole
                    or in part, or of any service, content, or feature offered through, or using, the
                    App.
                  </p>
                </li>
              </ol>
              <br>
              <ol start="4" class="list-decimal pl-4">
                <li>
                  <strong> I Understand that the App Belongs to Cargo Signal.</strong>
                  <p>
                    The App and (i) (a) its design, features, and functionality, (b) any materials,
                    software, code, files, content, information, reports, images, videos, or audio
                    contained in or generated by the App and the design, selection, and arrangement
                    thereof, (c) any accompanying data, (d) confidential information, (e) know how, and
                    (f) embedded software, including third-party software (collectively, &ldquo;<strong
                      ><u>Cargo Signal Content</u></strong
                    >&rdquo;)); (ii) documentation and accompanying fonts (collectively, &ldquo;<strong
                      ><u>Cargo Signal Documentation</u></strong
                    >&rdquo;); and (iii) any related intellectual property rights, including patents,
                    copyrights, trademarks, service marks, trade secrets, know how, confidential
                    information, manuals, processes, procedures, interfaces, features, and other similar
                    rights or protectable attributes (collectively, &ldquo;<strong
                      ><u>Cargo Signal Intellectual Property</u></strong
                    >&rdquo;), are owned by Cargo
                  </p>
                  <br>
                  <p>
                    Signal or its licensors and are protected by United States and international laws
                    and conventions, including those governing copyrights, trademarks, patents, and
                    trade-secrets. This Agreement does not transfer any title or rights to the App,
                    Cargo Signal Content, Cargo Signal Documentation, or Cargo Signal Intellectual
                    Property to you. Except for the limited licenses granted to you by this Agreement,
                    all title and rights to the App, the Cargo Signal Content, the Cargo Signal
                    Documentation, and the Cargo Signal Intellectual Property remain with Cargo Signal.
                    Cargo Signal reserves all rights not expressly granted to you, and except as
                    provided in this Agreement, the rights granted by this Agreement are
                    non-transferable and do not include any intellectual property rights. You understand
                    and acknowledge that Cargo Signal owns the App, the Cargo Signal Content, the Cargo
                    Signal Documentation, and the Cargo Signal Intellectual Property.
                  </p>
                  <br>
                  <p>
                    In addition, CARGO SIGNAL, CARGO SIGNAL SOLUTIONS, and certain other names, brands,
                    logos, slogans, trademarks, and service marks (collectively, &ldquo;<strong
                      ><u>Cargo Signal Marks</u></strong
                    >&rdquo;) are marks of Cargo Signal or its affiliates or licensors. Not all marks of
                    Cargo Signal will appear on the App.
                  </p>
                  <br>
                  <p>
                    The App, Cargo Signal Content, and Cargo Signal Documentation also are protected by
                    copyright. Neither the App nor any Cargo Signal Content or Cargo Signal
                    Documentation may be copied, reproduced, republished, uploaded, broadcast, posted,
                    transmitted, used to create derivative works, or distributed except as expressly
                    permitted by this Agreement. You agree to reproduce all trademarks, logos, copyright
                    notices, and other proprietary notices contained within the App, the Cargo Signal
                    Content, and the Cargo Signal Documentation in any authorized copies of the App, the
                    Cargo Signal Content, or the Cargo Signal Documentation.
                  </p>
                  <br>
                  <p>
                    Use of the App, Cargo Signal Content, or Cargo Signal Documentation in any other way
                    or for any purpose other than as permitted in this Agreement is a violation of Cargo
                    Signal&rsquo;s copyrights and other proprietary rights. Permission for all other
                    uses of the App, Cargo Signal Content, or Cargo Signal Documentation must be
                    obtained from Cargo Signal in advance and in writing. All content, interfaces,
                    graphics, code, design rights, and other intellectual property rights, in each case,
                    whether registered or unregistered, other than the marks of third parties, and
                    related goodwill are the property of Cargo Signal. Further, you acknowledge and
                    agree that the existence of a copyright notice on copies of the App, the Cargo
                    Signal Content, or the Cargo Signal Documentation is not an admission or presumption
                    that the publication of the App, Cargo Signal Content, or Cargo Signal Documentation
                    has occurred or is intended.
                  </p>
                  <br>
                  <p>
                    YOU UNDERSTAND THAT YOU HAVE NO RIGHTS TO THE APP, CARGO SIGNAL CONTENT, CARGO
                    SIGNAL DOCUMENTATION, CARGO SIGNAL MARKS, OR ANY OTHER CARGO SIGNAL PROPERTY EXCEPT
                    AS INDICATED IN THIS AGREEMENT. CARGO SIGNAL TAKES THE PROTECTION OF INTELLECTUAL
                    PROPERTY RIGHTS, INCLUDING COPYRIGHTS, VERY SERIOUSLY. CARGO SIGNAL WILL TERMINATE
                    YOUR ACCESS TO, OR USE OF, ALL OR ANY PART OF THE APP, WITHOUT NOTICE TO YOU, IF YOU
                    INFRINGE OR MISAPPROPRIATE THE INTELLECTUAL PROPERTY RIGHTS, INCLUDING COPYRIGHTS,
                    OF CARGO SIGNAL OR OTHERS.
                  </p>
                </li>
              </ol>
              <br>
              <ol start="5" class="list-decimal pl-4">
                <li>
                  <strong>
                    I Understand that I have Only a Limited Right to Use the App as Provided in this
                    Agreement.</strong
                  >
                  <p>
                    Solely to enable your use of the App for legitimate business purposes as a customer
                    of Cargo Signal in accordance with the Additional Terms and Conditions, subject to
                    third-party terms (as described below), Cargo Signal hereby grants you a
                    non-exclusive, limited, revocable, non-sub-licensable, non-transferrable license (i)
                    to use the App (as changed, modified, or upgraded by Cargo Signal from time to time
                    in its sole discretion) in accordance with the terms and conditions of this
                    Agreement and (ii) to view and display Cargo Signal Content (other than Cargo Signal
                    software source code) and Cargo Signal Documentation for your legitimate business
                    use, but only in connection with your use of the App. Except as provided otherwise
                    in the Additional Terms and Conditions, when you use or access the App, you are not
                    receiving any other rights from us, including intellectual property or other
                    proprietary rights of Cargo Signal. In addition, this Agreement does not entitle you
                    to receive, and does not obligate Cargo Signal to provide, support, telephone
                    assistance, or enhancements or updates to the App.
                  </p>
                  <br>
                  <p>
                    In accordance with this license, I understand, acknowledge, represent, and agree
                    that I am allowed to:
                  </p>
                  <br>
                  <ol class="list-decimal pl-4">
                    <li>
                      use the App on one individual computer user device and one individual mobile user
                      device;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="2">
                    <li>
                      temporarily store copies of the App and Cargo Signal Content in RAM incidental to
                      my accessing and viewing those materials;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="3">
                    <li>
                      store files that are automatically cached by my web browser for display
                      enhancement purposes;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="4">
                    <li>
                      print or download 1 copy of the Cargo Signal Documentation for my own, legitimate
                      business use; and
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="5">
                    <li>
                      print or download a reasonable number of pages of the App for my own, legitimate
                      business use.
                    </li>
                  </ol>
                  <br>
                  <p>
                    I understand, acknowledge, represent, and agree that unless expressly permitted by
                    this Agreement or the Additional Terms and Conditions, I am not allowed to:
                  </p>
                 <br>
                  <ol class="list-decimal pl-4">
                    <li>
                      upload the App, Cargo Signal Content, or Cargo Signal Documentation to the
                      internet;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="2">
                    <li>
                      make the App, Cargo Signal Content, or Cargo Signal Documentation available on the
                      internet for other persons or entities to copy, download, or use;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="3">
                    <li>
                      make the App, Cargo Signal Content, or Cargo Signal Documentation available over a
                      network where it could be used on multiple devices or by multiple users at the
                      same time;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="4">
                    <li>
                      use any of Cargo Signal Intellectual Property in the design, development,
                      manufacture, licensing, or distribution of any devices, accessories, software, or
                      services for use with the App;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="5">
                    <li>
                      resell, transfer, modify, copy, duplicate, create, or re-create the App, programs,
                      offerings, services, or other aspects of the App, the Cargo Signal Content, or the
                      Cargo Signal Documentation;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="6">
                    <li>
                      amend, alter, change, or modify the App, the Cargo Signal Content, or the Cargo
                      Signal Documentation;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="7">
                    <li>
                      remove, mutilate, modify, or otherwise change or delete any trademark, logo,
                      copyright notice, or other proprietary notice contained within the App, the Cargo
                      Signal Content, or the Cargo Signal Documentation;
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="8">
                    <li>
                      disassemble, decompile, reverse engineer, or otherwise attempt to discovery the
                      source code of the App; or
                    </li>
                  </ol>
                  <ol class="list-decimal pl-4" start="9">
                    <li>
                      use any illustrations, photos, video or audio sequences, or any graphics
                      separately from the accompanying text.
                    </li>
                  </ol>
                </li>
              </ol>
              <br>
              <ol start="6" class="list-decimal pl-4">
                <li>
                  <strong>
                    I Understand that Cargo Signal May Collect Certain Information About Me When I Use
                    the App.</strong
                  >
                  <p>
                    While using the App, Cargo Signal through the App may collect technical data and
                    related information, system, application software, and peripherals, that is gathered
                    periodically to facilitate the provision of software updates, product support, and
                    other services to you (if any) related to such App. When you use the App, the App
                    may automatically collect and store some or all of the following information from
                    your mobile or GPS container tracking device, including without limitation your
                    preferred language and country site (if applicable); your phone number or other
                    unique device identifier assigned to your mobile or GPS container device; the IP
                    address of your mobile or GPS container device; the manufacturer and model of your
                    mobile or GPS container device; mobile or GPS container operating system; type of
                    Internet browsers you are using; and information about how you interact with the
                    App, such as how many times you use a specific part of the App over a given time
                    period, the amount of time you spend using the App, how often you use the App,
                    actions you take in the App, how you engage with the App and any other information
                    tied directly or indirectly to your device (&ldquo;<strong
                      ><u>Personal Information</u></strong
                    >&rdquo;). Your use of the App constitutes your consent to allow Cargo Signal to
                    record and use your Personal Information. Read the <u>Privacy Statement</u> to
                    understand what Cargo Signal does with your Personal Information and how to exercise
                    your rights to your Personal Information.
                  </p>
                </li>
              </ol>
              <br>
              <ol class="list-decimal pl-4" start="7" class="list-decimal pl-4">
                <li>
                  <strong>
                    I Will Not Use the App Unless I Agree to, and Follow, Each of These Rules:</strong
                  >
                  <br>
                  <ol class="list-alpha pl-4" type="A">
                    <p>
                      <li>
                        <strong> User Accounts:</strong>
                        <p>
                          In order to use certain features of the App, you are required to register by
                          providing Cargo Signal with certain User Content (as defined below), as
                          requested by Cargo Signal, including your user name, business name, email
                          address, and an optional password you will use to access the App. Cargo Signal
                          reserves the right to refuse registration or cancel a user name or an account,
                          in its sole discretion. Accordingly, you understand, acknowledge, represent,
                          and agree as follows:
                        </p>
                        <br>
                        <ol class="list-decimal pl-4">
                          <li>I will create only one user account for myself.</li>
                        </ol>
                        <ol class="list-decimal pl-4" start="2">
                          <li>
                            I will provide accurate, truthful, current, and complete information when
                            creating my account.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="3">
                          <li>
                            I will not create an account for anyone other than myself without
                            permission.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="4">
                          <li>
                            I will maintain and promptly update my account information when any of my
                            account information changes.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="5">
                          <li>
                            I will maintain the security of my account by not sharing my account
                            information with any other person.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="6">
                          <li>I will protect the confidentiality of my password.</li>
                        </ol>
                        <ol class="list-decimal pl-4" start="7">
                          <li>I will not share my password or let anyone else access my account.</li>
                        </ol>
                        <ol class="list-decimal pl-4" start="8">
                          <li>I will not use another person&rsquo;s account without permission.</li>
                        </ol>
                        <ol class="list-decimal pl-4" start="9">
                          <li>
                            I will not solicit account information from other users or access another
                            user&rsquo;s account.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="10">
                          <li>
                            I will take responsibility for all activities that occur under my account
                            and for all User Content (defined below) posted using my account.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="11">
                          <li>I will accept all risks of unauthorized access of my account.</li>
                        </ol>
                        <ol class="list-decimal pl-4" start="12">
                          <li>
                            I will immediately notify Cargo Signal in writing of any unauthorized use of
                            my account of which I become aware.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="13">
                          <li>
                            I may be liable for the losses of Cargo Signal or others due to the
                            unauthorized access and use of my account.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="14">
                          <li>
                            I have no property rights or ownership interests in and to my account, and
                            all rights to my account belong to Cargo Signal.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="15">
                          <li>
                            Cargo Signal may disable, suspend, or terminate my account at any time in
                            its sole discretion and without any notice to me.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="16">
                          <li>
                            Any violation of this Agreement may result in the termination of my account.
                          </li>
                        </ol>
                        <ol class="list-decimal pl-4" start="17">
                          <li>
                            If my account is disabled, suspended, or terminated by Cargo Signal, I will
                            not create another one without Cargo Signal&rsquo;s express permission.
                          </li>
                        </ol>
                      </li>
                    </p>
                  </ol>

                  <ol class="list-alpha pl-4" type="A" start="2">
                    <li>
                      <strong> Safe Use of the App:</strong>
                      <p>
                        The safety of all App users is important. In addition, Cargo Signal believes
                        that all users benefit from basic rules regarding conduct while using the App.
                        Accordingly, you understand, acknowledge, represent, and agree as follows:
                      </p>
                      <br>
                      <ol class="list-decimal pl-4">
                        <li>
                          I will not use the App to transmit viruses, worms, bots, Trojans, or other
                          malicious code.
                        </li>
                      </ol>
                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          I will not do anything that could overburden, disable, or impair the
                          appearance, functionality, or proper working of the App or any software,
                          hardware, equipment, or materials used in connection with the App.
                        </li>
                      </ol>
                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          I will not use or exploit any errors in design, features that have not been
                          documented, or &ldquo;bugs&rdquo; to gain access in any way that is not
                          generally known and intentionally made available by Cargo Signal, and I will
                          promptly report to Cargo Signal my discovery of any such errors, features, or
                          bugs.
                        </li>
                      </ol>
                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          I will not hack the App, spoof the App, or modify any other software or
                          website to imply falsely that the other software or website is associated with
                          Cargo Signal or the App.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="5">
                        <li>
                          I will not collect user account information or User Content (as defined
                          below), or otherwise access the App through an automated means (i.e., through
                          bots, robots, spiders, scrapers, etc.).
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="6">
                        <li>
                          I will not collect or store personal information about any other individual
                          using the App, or otherwise stalk, repeatedly contact, or harass other App
                          users.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="7">
                        <li>
                          I will not use the App, Cargo Signal Content, or Cargo Signal Documentation in
                          any way that violates any applicable federal, state, local, or international
                          law or regulation, including without limitation, any laws regarding the export
                          of data or software to and from the United States or other countries and any
                          laws regarding privacy, online conduct, or acceptable online content.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="8">
                        <li>I will not encourage or facilitate any violations of this Agreement.</li>
                      </ol>
                      <br>
                      <p>
                        We reserve the right (but not the obligation) in our sole discretion to refuse,
                        delete, suspend, or terminate&mdash;without notice&mdash;your access to or use
                        of the App, Cargo Signal Content, or Cargo Signal Documentation upon a violation
                        or attempted violation of this Agreement, including, without limitation, any of
                        the rules listed above. Indirect or attempted violations of this Agreement, and
                        actual or attempted violations by a third party acting on your behalf, shall be
                        deemed violations by you of this Agreement. Furthermore, it is a violation of
                        this Agreement to use the services of another company for the purpose of
                        facilitating any of the activities that violate this Agreement if such use of
                        another company&rsquo;s service could reasonably be expected to adversely affect
                        the App in any manner.
                      </p>
                    </li>
                  </ol>
                  <br>
                  <ol class="list-alpha pl-4" type="A" start="3">
                    <li>
                      <strong> Posting User Content:</strong>
                      <p>
                        The App may allow you to enter, post, submit, or link to certain content
                        (including, for example, user shipping information, logistics information or
                        other supporting information, and the App may generate data (including shipping
                        and logistics information) that is personal to you (collectively, &ldquo;<strong
                          ><u>User Content</u></strong
                        >&rdquo;). I agree, acknowledge, and understand that all User Content that I
                        supply to Cargo Signal App will comply with the terms and conditions of this
                        Agreement. I represent and warrant the accuracy and completeness of all User
                        Content supplied to Cargo Signal in connection with my use of the App.
                      </p>
                      <br>
                      <p>
                        Cargo Signal does not claim any ownership rights in any User Content.
                        Accordingly, you are free to use, sell, transfer, display, and create derivative
                        works from your raw User Content so long as you first extract your raw User
                        Content from the App or any report generated by the App and use your raw User
                        Content in a form and format other than as provided by the App or any report
                        generated by the App. For the sake of clarity, Cargo Signal considers its
                        reports proprietary Cargo Signal Content, and your use of those reports is
                        limited by these EULA terms.
                      </p>
                      <br>
                      <p>
                        While you retain ownership of User Content, you grant Cargo Signal, and Cargo
                        Signal hereby accepts, a worldwide, perpetual, irrevocable, non-exclusive,
                        transferrable, sub-licensable, fully-paid up, and royalty free license to use,
                        display, reproduce, create derivative works from, combine with other works,
                        alter, modify, delete from, add to, translate, and transfer your User Content.
                        Except when your User Content is combined in the aggregate with other App
                        users&rsquo; User Content, Cargo Signal will not disclose your User Content to
                        any third party without your permission.
                      </p>
                      <br>
                      <p>
                        In addition, by entering, posting, submitting, providing, or linking to User
                        Content, you hereby release Cargo Signal from any claims that such use, as
                        authorized above, violates any rights which you may have under applicable law,
                        and you understand that you will not be entitled to any compensation for any use
                        of your User Content. Further, by entering, posting, submitting, providing, or
                        linking to User Content, you hereby understand, acknowledge, represent, and
                        agree as follows:
                      </p>
                      <br>
                      <ol class="list-decimal pl-4">
                        <li>
                          I own and control all of the rights to the User Content or otherwise have (a)
                          the legal rights to enter, post, submit, provide, or link to the User Content
                          using the App and (b) the right to grant Cargo Signal the license specified in
                          this Agreement.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          The User Content that I enter, post, submit, provide, or link to does not, and
                          will not, result in a breach of contract between me and any other person.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          I will pay all royalties, fees, and any other monies owing to any person due
                          to any of the User Content that I enter, post, submit, provide, or link to
                          using the App.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          The User Content that I enter, post, submit, provide, or link to does not
                          violate the privacy rights, publicity rights, copyrights, contract rights,
                          intellectual property rights, or any other rights of any person.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="5">
                        <li>
                          I am solely responsible for all of the User Content that I enter, post,
                          submit, provide, link to, or otherwise make available in any way by or through
                          the App.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="6">
                        <li>
                          I will not use any Cargo Signal Intellectual Property without Cargo
                          Signal&rsquo;s permission.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="7">
                        <li>
                          Cargo Signal can, in its sole discretion, monitor User Content that I and
                          other users enter, post, submit, provide, or link to using the App.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="8">
                        <li>
                          Cargo Signal can, without notice and in its sole discretion, remove any User
                          Content that it believes is obscene, offensive, libelous, defamatory, or
                          violates the law or any of the terms and conditions of this Agreement.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="9">
                        <li>
                          Cargo Signal can, in its sole discretion, remove any User Conduct if it
                          believes that doing so will improve the App.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="10">
                        <li>
                          Cargo Signal can disclose my identity or other information about me to any
                          third party who claims that my User Content violates their rights, including
                          their intellectual property rights or their right to privacy.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="11">
                        <li>
                          Cargo Signal can take appropriate legal action against me, including without
                          limitation, referral to law enforcement, for any illegal or unauthorized use
                          of the App, Cargo Signal Content, or Cargo Signal Documentation.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="12">
                        <li>
                          Cargo Signal reserves the right to force forfeiture of the username of any
                          account that becomes inactive or any username that violates the trademark or
                          other intellectual property rights of any person.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="13">
                        <li>
                          Cargo Signal can suspend or terminate my use of the App if I fail to follow
                          the rules in this Agreement.
                        </li>
                      </ol>

                      <p>WITHOUT LIMITING THE FOREGOING, CARGO SIGNAL HAS THE RIGHT TO FULLY</p>
                      <p>COOPERATE WITH ANY LAW ENFORCEMENT AUTHORITIES OR COURT ORDERS</p>
                      <p>REQUESTING OR DIRECTING US TO DISCLOSE YOUR IDENTITY, USER ACCOUNT</p>
                      <p>
                        INFORMATION, OR ANY USER CONTENT YOU HAVE PROVIDED ON OR THROUGH THE APP. YOU
                        WAIVE AND HOLD HARMLESS CARGO SIGNAL, ITS AFFILIATES, AND ITS SERVICE PROVIDERS
                        FROM ANY CLAIMS OR CAUSES OF ACTION RESULTING FROM ANY ACTION TAKEN BY THE
                        FOREGOING PARTIES DURING, OR TAKEN AS A CONSEQUENCE OF, INVESTIGATIONS BY EITHER
                        CARGO SIGNAL OR LAW ENFORCEMENT AUTHORITIES.
                      </p>
                    </li>
                  </ol>
                  <br>
                  <ol class="list-alpha pl-4" type="A" start="4">
                    <li>
                      <strong> Links to Other Websites:</strong>
                      <p>
                        If the App contains links to other sites and resources provided by third
                        parties, these links are provided for your convenience only. This includes links
                        directing to steamship lines, customs authorities, etc. We have no control over
                        the contents of those sites or resources, and we accept no responsibility for
                        them or for any loss or damage that may arise from your use of them. If you
                        decide to access any of the third-party websites linked to this App, you agree
                        to do so at your own risk and subject to the terms and conditions of those
                        websites.
                      </p>
                    </li>
                  </ol>
                </li>
              </ol>
              <br>
              <ol class="list-decimal pl-4" start="8" class="list-decimal pl-4">
                <li>
                  <strong>
                    I Understand that the App Contains Third-Party Software Components that are Governed
                    by Additional Terms.</strong
                  >
                  <p>
                    The App contains various third-party software components. The purpose of these
                    software components is to enhance the functionality of the App and help provide
                    Cargo Signal with information regarding aggregate usage trends and market
                    information. When you use the App, you are agreeing to the terms and conditions,
                    including the privacy policies, associated with these third party applications, or,
                    when applicable, to Cargo Signal&rsquo;s agreement to those terms and conditions.
                    These third-party terms and conditions are separate and apart from this Agreement,
                    but they apply to you simultaneously. It is your responsibility to read, understand,
                    and abide by all third-party terms and conditions. It is also your responsibility to
                    fully understand the privacy implications of all third-party terms and conditions.
                  </p>

                  <p>
                    The third party applications included in the App include, but are not limited to,
                    the following:
                  </p>
                  <br>
                  <ol class="list-decimal pl-4">
                    <li>
                      <strong>a) Google Maps. </strong>Electronic mapping software.
                      <a class="text-blue-600 hover:cursor-pointer hover:underline" href="https://www.google.com/work/earthmaps/legal/amer/premium-maps-terms.html"
                        >https://www.google.com/work/earthmaps/legal/amer/premimum-maps-terms.html</a
                      >
                    </li>
                  </ol>
                  <br>
                  <p>
                    Cargo Signal may be using other third party applications and software in the App. It
                    is your responsibility to ensure that you comply with any third party terms and that
                    you are aware of any third party privacy policies that may apply to you, in
                    conjunction with this Agreement.
                  </p>
                  <br>
                  <p>
                    The App also may contain links to other sites, applications, or content, which links
                    may appear to embed the sites, applications, or content hosted or provided by third
                    parties into the interface of the App. Your use of such other sites, applications,
                    or content is subject to the terms of use, if any, governing the use of such sites,
                    applications, or content. Cargo Signal is not able to control these third party
                    sites, applications, or content, and assumes no responsibility for their subject
                    matter, privacy policies, or practices. If there is any conflict between this
                    Agreement and any terms or conditions set forth with respect to any sites,
                    applications, or content provided by any third parties, then the terms of such third
                    party sites, applications, or content will control your use of such sites,
                    applications, or content. Please review the terms of use for each third-party
                    provider of sites, applications, or content so that you understand all of the terms
                    and conditions that will apply to you. By using the App, you expressly relieve Cargo
                    Signal from any and all liability arising from your use of any third-party sites,
                    applications, or content.
                  </p>
                </li>
              </ol>
              <br>
              <ol class="list-decimal pl-4" start="9" class="list-decimal pl-4">
                <li>
                  <strong> I Also Agree to the Following Terms and Conditions:</strong>
                  <ol class="list-alpha pl-4" type="A">
                    <p>
                      <li>
                        <strong> Confidentiality:</strong>
                        <p>
                          You acknowledge and understand that certain information relating to Cargo
                          Signal&rsquo;s business operations, including, without limitation, the App,
                          Cargo Signal Content, Cargo Signal Documentation, object and source codes,
                          operational processes, certain financial information, or other information,
                          which is highly proprietary and confidential to Cargo Signal (hereafter
                          &ldquo;<strong><u>Confidential Information</u></strong
                          >&rdquo;) may be shared by Cargo Signal in both verbal and written or other
                          tangible form during the execution of the parties&rsquo; respective
                          obligations under this Agreement. You agree you will use the Confidential
                          Information only in connection with your performance of your obligations under
                          this Agreement, and you will not disclose or show any Confidential Information
                          to any other individuals including third parties, except as may be authorized
                          by Cargo Signal or required by governmental organizations, laws, or court
                          orders. You agree that you will not use any Confidential Information in any
                          manner to compete with or assist in competing with Cargo Signal. However, you
                          will not be liable for misuse or disclosure of Confidential Information
                          furnished to you if you can demonstrate with reasonable certainty that at the
                          time of disclosure: (a) such information was made available and accessible to
                          the public other than by you or your representatives; (b) such information has
                          become public knowledge through no fault of your own; or (c) such information
                          was in your possession at the time of disclosure as evidenced by written or
                          other tangible evidence in your possession. Upon the expiration or termination
                          of this Agreement for any reason, you will immediately return or destroy all
                          Confidential Information without retaining copies thereof. You agree that the
                          restrictions of this section shall be in force for the length of this
                          Agreement and shall remain in effect for three (3) years following the
                          expiration or termination of this Agreement.
                        </p>
                      </li>
                    </p>
                  </ol>

                  <ol class="list-alpha pl-4" type="A" start="2">
                    <li>
                      <strong> Terms Specific to Apple Applications:</strong>
                      <p>
                        In addition to the other provisions of this Agreement, the following terms and
                        conditions apply to software applications made available by Cargo Signal that
                        are intended to be operated in connection with products made commercially
                        available by Apple Inc. (&ldquo;<strong><u>Apple</u></strong
                        >&rdquo;) (each, an &ldquo;<strong><u>Apple Application</u></strong
                        >&rdquo;):
                      </p>

                      <ol class="list-decimal pl-4">
                        <li>
                          You and Cargo Signal acknowledge that this Agreement is concluded between You
                          and Cargo Signal, and not with Apple, and Cargo Signal, not Apple, is solely
                          responsible for the Apple Application and the content thereof.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          You may not use the Apple Application in any manner that violates, or
                          otherwise is in conflict with, the Apple Media Services Terms and Conditions
                          (the &ldquo;<strong><u>Apple Terms</u></strong
                          >&rdquo;), a copy of which is available at
                          <a href="http://www.apple.com/legal/internet-services/itunes/us/terms.html"
                            >http://www.apple.com/legal/internet-services/itunes/us/terms.html</a
                          >.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          You acknowledge that you have had an opportunity to review the Apple Terms.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          The license granted to you for the Apple Application is limited to a
                          non-transferable license to use the Apple Application on any iPhone, iPad,
                          iPod touch, or Mac that you or your organization owns or controls as permitted
                          by the Apple Terms.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="5">
                        <li>
                          You and Cargo Signal acknowledge that Apple has no obligation whatsoever to
                          furnish any maintenance and support services with respect to the Apple
                          Application.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="6">
                        <li>
                          Apple is not responsible for any product warranties, whether express or
                          implied by law. In the event of any failure of the Apple Application to
                          conform to any applicable warranty, you may notify Apple, and Apple will
                          refund the purchase price for the Apple Application to you; and, to the
                          maximum extent permitted by applicable law, Apple will have no other warranty
                          obligation whatsoever with respect to the Apple Application, and any other
                          claims, losses, liabilities, damages, costs or expenses attributable to any
                          failure to conform to any warranty will be Cargo Signal&rsquo;s sole
                          responsibility, to the extent such warranty or responsibility cannot be
                          disclaimed under applicable law.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="7">
                        <li>
                          You and Cargo Signal acknowledge that Cargo Signal, not Apple, is responsible
                          for addressing any claims of you or any third party relating to the Apple
                          Application or your possession or use of that the Apple Application,
                          including, but not limited to: (i) product liability claims; (ii) any claim
                          that the Apple Application fails to conform to any applicable legal or
                          regulatory requirement; and (iii) claims arising under consumer protection or
                          similar legislation.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="8">
                        <li>
                          You and Cargo Signal acknowledge that, in the event of any third party claim
                          that the Apple
                        </li>
                        <li>
                          Application or the end-user&rsquo;s possession and use of that Apple
                          Application infringes that third party&rsquo;s intellectual property rights,
                          Cargo Signal, not Apple, will be solely responsible for the investigation,
                          defense, settlement, and discharge of any such intellectual property
                          infringement claim.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="10">
                        <li>
                          You represent and warrant that (i) you are not located in a country that is
                          subject to a U.S. Government embargo, or that has been designated by the U.S.
                          Government as a &ldquo;terrorist supporting&rdquo; country; and (ii) you are
                          not listed on any U.S. Government list of prohibited or restricted parties.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="11">
                        <li>
                          You and Cargo Signal acknowledge and agree that Apple, and Apple&rsquo;s
                          subsidiaries, are third party beneficiaries of this Agreement with respect to
                          the Apple Application, and that, upon your acceptance of this Agreement, Apple
                          will have the right (and will be deemed to have accepted the right) to enforce
                          this Agreement against you with respect to the Apple Application as a third
                          party beneficiary thereof.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="12">
                        <li>
                          Any questions, complaints, or claims with respect to the Apple-Enabled
                          Application should be directed to Cargo Signal as follows:
                          <p>
                            Cargo Signal Solutions, LLC <br />
                            1015 Third Avenue <br />
                            Seattle, Washington 98104 <br />
                            Phone: 206-834-8000 <br />
                          </p>
                        </li>
                      </ol>
                    </li>
                  </ol>

                  <ol class="list-alpha pl-4" type="A" start="3">
                    <li>
                      <strong> Terms Specific to Android Applications:</strong>
                      <p>
                        In addition to the other provisions of this Agreement, the following terms and
                        conditions apply to software applications made available by Cargo Signal that
                        are intended to be operated in connection with products made commercially
                        available through the Android store and/or Google Play. (each, an &ldquo;<strong
                          ><u>Android Application</u></strong
                        >&rdquo;):
                      </p>
                      <ol class="list-decimal pl-4">
                        <li>
                          You and Cargo Signal acknowledge that this Agreement is concluded between You
                          and Cargo Signal, and not with Android Application, and Cargo Signal, not
                          Google, is solely responsible for the Android Application and the content
                          thereof.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          You may not use the Android Application in any manner that violates, or
                          otherwise is in conflict with, the Google Play Terms of Service (the
                          &ldquo;<strong><u>Android Terms</u></strong
                          >&rdquo;), a copy of which is available at
                          <a href="http://play.google.com/intl/hl_in/about/play-terms.html"
                            >http://play.google.com/intl/hl_in/about/play-terms.html</a
                          >.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          You acknowledge that you have had an opportunity to review the Android Terms.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          The license granted to you for the Android Application is limited to a
                          non-transferable license.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="5">
                        <li>
                          You and Cargo Signal acknowledge that Google has no obligation whatsoever to
                          furnish any maintenance and support services with respect to the Android
                          Application.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="6">
                        <li>
                          Google is not responsible for any product warranties, whether express or
                          implied by law. In the event of any failure of the Android Application to
                          conform to any applicable warranty, you may notify Google, and Google will
                          refund the purchase price for the Android Application to you; and, to the
                          maximum extent permitted by applicable law, Google will have no other warranty
                          obligation whatsoever with respect to the Google Application, and any other
                          claims, losses, liabilities, damages, costs or expenses attributable to any
                          failure to conform to any warranty will be Cargo Signal&rsquo;s sole
                          responsibility, to the extent such warranty or responsibility cannot be
                          disclaimed under applicable law.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="7">
                        <li>
                          You and Cargo Signal acknowledge that Cargo Signal, not Google, is responsible
                          for addressing any claims of you or any third party relating to the Android
                          Application or your possession or use of that the Android Application,
                          including, but not limited to: (i) product liability claims; (ii) any claim
                          that the Android Application fails to conform to any applicable legal or
                          regulatory requirement; and (iii) claims arising under consumer protection or
                          similar legislation.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="8">
                        <li>
                          You and Cargo Signal acknowledge that, in the event of any third party claim
                          that the Android Application or the end-user&rsquo;s possession and use of
                          that Android Application infringes that third party&rsquo;s intellectual
                          property rights, Cargo Signal, not Google, will be solely responsible for the
                          investigation, defense, settlement, and discharge of any such intellectual
                          property infringement claim.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="9">
                        <li>
                          You represent and warrant that (i) you are not located in a country that is
                          subject to a U.S. Government embargo, or that has been designated by the U.S.
                          Government as a &ldquo;terrorist supporting&rdquo; country; and (ii) you are
                          not listed on any U.S. Government list of prohibited or restricted parties.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="10">
                        <li>
                          You and Cargo Signal acknowledge and agree that Google, and Google&rsquo;s
                          subsidiaries, are third party beneficiaries of this Agreement with respect to
                          the Android Application, and that, upon your acceptance of this Agreement,
                          Google will have the right (and will be deemed to have accepted the right) to
                          enforce this Agreement against you with respect to the Android Application as
                          a third party beneficiary thereof.
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="11">
                        <li>
                          Any questions, complaints, or claims with respect to the Application should be
                          directed to Cargo Signal as follows:
                          <p>
                            Cargo Signal Solutions, LLC <br />
                            1015 Third Avenue <br />
                            Seattle, Washington 98104 <br />
                            Phone: 206-834-8000 <br />
                          </p>
                        </li>
                      </ol>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="4">
                    <li>
                      <strong>
                        Protecting Copyrights Under the Digital Millennium Copyright Act:</strong
                      >
                      <p>
                        Pursuant to the Digital Millennium Copyright Act, Title 17, United States Code,
                        Section 501 et seq., copyright owners or their authorized agents may submit a
                        complaint of alleged copyright infringement to Cargo Signal if they have a
                        good-faith belief that their protected works are being infringed. Cargo Signal
                        will respond to all such notifications that are sent to:
                      </p>

                      <p>
                        Cargo Signal Solutions, LLC <br />
                        1015 Third Avenue <br />
                        Seattle, Washington 98104 <br />
                        Phone: 206-834-8000 <br />
                      </p>

                      <p>
                        To be effective, the notification must be a written communication that includes:
                      </p>

                      <ol class="list-decimal pl-4">
                        <li>
                          a physical or electronic signature of a person authorized to act on behalf of
                          the owner of a right that is allegedly infringed;
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          identification of the copyrighted work claimed to have been infringed, or, if
                          multiple copyrighted works at a single site are covered by a single
                          notification, a representative list of such works at such site;
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          identification of the material that is claimed to be infringing or to be the
                          subject of infringing activity and that is to be removed or access to which is
                          to be disabled, and information reasonably sufficient to permit the provider
                          to locate the material;
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          information reasonably sufficient to permit the provider to contact the
                          complaining party, such as an address, telephone number, and, if available, an
                          electronic mail address at which the complaining party may be contacted;
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="5">
                        <li>
                          a statement that the complaining party has a good-faith belief that use of the
                          material in the manner complained of is not authorized by the copyright owner,
                          its agent, or the law; and
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="6">
                        <li>
                          a statement that the information in the notification is accurate, and under
                          penalty of perjury, that the complaining party is authorized to act on behalf
                          of the owner of an exclusive right that is allegedly infringed.
                        </li>
                      </ol>

                      <p>
                        Upon receipt of such notification, Cargo Signal shall promptly investigate the
                        matter, and, if appropriate, remove or disable access to the allegedly
                        infringing material. After Cargo Signal removes or disables access to such
                        material, if such material was posted by a user of the App, Cargo Signal will
                        notify the party that posted the material of its action. Such party may then
                        provide Cargo Signal&rsquo;s designated agent proper
                        &ldquo;counter-notification&rdquo; stating his, her, or its authority to post
                        the allegedly infringing material, which Cargo Signal will forward to the
                        alleged copyright owner. Cargo Signal will inform the alleged copyright owner
                        that Cargo Signal will repost the removed material or cease disabling access to
                        it in fourteen (14) business days unless the alleged copyright owner sends a
                        second notification to Cargo Signal&rsquo;s designated agent stating that it has
                        filed suit against the alleged infringer.
                      </p>

                      <p>
                        An effective counter-notification must be sent to Cargo Signal&rsquo;s
                        designated agent, whose name and address are listed above. The notice must
                        include the following information:
                      </p>

                      <ol class="list-decimal pl-4">
                        <li>the counter-notifying party&rsquo;s physical or electronic signature;</li>
                      </ol>

                      <ol class="list-decimal pl-4" start="2">
                        <li>
                          identification of the material that has been removed or disabled and the
                          location where the material was before its removal or disablement;
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="3">
                        <li>
                          a statement, under penalty of perjury, that the party has a good faith belief
                          that the material was removed or disabled as a result of mistake or
                          misidentification; and
                        </li>
                      </ol>

                      <ol class="list-decimal pl-4" start="4">
                        <li>
                          the counter-notifying party&rsquo;s name, address, phone number, and a
                          statement that the party consents to the jurisdiction of the federal court in
                          its district, or if the subscriber is foreign, it consents to any jurisdiction
                          where Cargo Signal is subject to jurisdiction, and that the subscriber will
                          accept service of process from the person who provided Cargo Signal with
                          notification or an agent of such a person.
                        </li>
                      </ol>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="5">
                    <p>
                      <li>
                        <strong> Liability and Warranty Disclaimer:</strong>
                        <p>
                          YOU EXPRESSLY ACKNOWLEDGE THAT USE OF THE APP IS AT YOUR SOLE RISK AND THAT
                          THE ENTIRE RISK AS TO SATISFACTORY QUALITY, PERFORMANCE, ACCURACY, AND EFFORT
                          IS WITH YOU. IN ADDITION, YOU ASSUME THE TOTAL RISK AND RESPONSIBILITY FOR THE
                          APP AND UNDERSTAND AND ACKNOWLEDGE THAT ANY LOSS OF DATA, DATA ERRORS, DAMAGE
                          TO YOUR COMPUTER SYSTEM, SHIPMENT DELAYS, INCOMPLETE TRANSACTIONS, SYSTEM
                          DOWNTIMES, OR ANY OTHER ISSUES YOU EXPERIENCE USING THE APP ARE AT YOUR SOLE
                          RISK AND ARE NOT THE RESPONSIBILITY OF CARGO SIGNAL. TO THE MAXIMUM EXTENT
                          PERMITTED BY APPLICABLE LAW, THE APP AND ANY SERVICES PERFORMED OR PROVIDED BY
                          OR THROUGH THE APP (&ldquo;<strong><u>SERVICES</u></strong
                          >&rdquo;) ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;,
                          WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND, AND ARE SUBJECT TO CHANGE AT
                          ANY TIME WITHOUT NOTICE TO YOU. YOU UNDERSTAND AND AGREE THAT THE APP WILL NOT
                          BE UNINTERRUPTED OR ERROR FREE AND THAT CARGO SIGNAL MAY NOT CORRECT ALL
                          ERRORS.
                        </p>

                        <p>
                          EXCEPT AS PROHIBITED BY LAW OR PROVIDED OTHERWISE IN THIS AGREEMENT OR THE
                          ADDITIONAL TERMS AND CONDITIONS, CARGO SIGNAL DOES NOT MAKE ANY, AND HEREBY
                          DISCLAIMS ALL, WARRANTIES AND CONDITIONS WITH RESPECT TO THE APP AND ANY
                          SERVICES, EITHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING, BUT NOT LIMITED TO,
                          THE IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY, OF SATISFACTORY
                          QUALITY, OF FITNESS FOR A PARTICULAR PURPOSE, OF ACCURACY, OF QUIET ENJOYMENT,
                          AND OF NON-INFRINGEMENT OF THIRD PARTY RIGHTS. CARGO SIGNAL DOES NOT WARRANT
                          AGAINST INTERFERENCE WITH YOUR ENJOYMENT OF THE APP, THAT THE FUNCTIONS
                          CONTAINED IN THE APP OR SERVICES PROVIDED WILL MEET YOUR REQUIREMENTS, THAT
                          THE OPERATION OF THE APP OR SERVICES WILL BE UNINTERRUPTED, SECURE, OR
                          ERROR-FREE, OR THAT DEFECTS IN THE APP OR SERVICES WILL BE CORRECTED. LOCATION
                          DATA THAT THE APP PROVIDES IS AN APPROXIMATION AND SHOULD NOT BE RELIED ON
                          WHERE INACCURATE OR INCOMPLETE LOCATION DATA MAY LEAD TO DEATH, PERSONAL
                          INJURY, PROPERTY DAMAGE, OR ENVIRONMENTAL DAMAGE. NO ORAL OR WRITTEN
                          INFORMATION OR ADVICE GIVEN BY CARGO SIGNAL OR ITS AUTHORIZED REPRESENTATIVES
                          SHALL CREATE A WARRANTY. MOREOVER, NOT ALL THE FUNCTIONALITY AND FEATURES SET
                          FORTH IN THIS AGREEMENT WILL NECESSARILY BE AVAILABLE IN THE INITIAL VERSION
                          (OR ANY FUTURE VERSION) OF THE APP, AND NO MENTION OF SUCH FUNCTIONALITY OR
                          FEATURES HEREIN SHOULD BE CONSTRUED AS A PROMISE TO PROVIDE SUCH FUNCTIONALITY
                          OR FEATURES IN THE APP OR IN ANY OTHER PRODUCT OFFERED BY CARGO SIGNAL. SOME
                          JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES OR LIMITATIONS
                          ON APPLICABLE STATUTORY RIGHTS OF A CONSUMER, SO THE ABOVE EXCLUSION AND
                          LIMITATIONS MAY NOT APPLY TO YOU.
                        </p>

                        <p>EXCEPT AS PROHIBITED BY LAW OR PROVIDED IN THE ADDITIONAL TERMS AND</p>
                        <p>
                          CONDITIONS, IN NO EVENT SHALL CARGO SIGNAL, ITS AFFILIATES, OR THEIR
                          LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE
                          LIABLE FOR ANY INCIDENTAL, SPECIAL, INDIRECT, CONSEQUENTIAL, OR PUNITIVE
                          DAMAGES WHATSOEVER, INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF
                          PROFITS, LOSS OF DATA, LOSS OF USE, BUSINESS INTERRUPTION, PROCUREMENT OF
                          SUBSTITUTE GOODS OR SUBSTITUTE SERVICES, OR ANY OTHER COMMERCIAL DAMAGES OR
                          LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE APP,
                          HOWEVER CAUSED, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT OR
                          OTHERWISE) AND EVEN IF CARGO SIGNAL IS AWARE OF THE POSSIBILITY OF SUCH
                          DAMAGES. THE LIMITATIONS ON LIABILITY SET FORTH HEREIN ARE FUNDAMENTAL
                          ELEMENTS OF THIS AGREEMENT. YOU AND CARGO SIGNAL HEREBY ACKNOWLEDGE AND AGREE
                          THAT BOTH YOU AND CARGO SIGNAL WOULD NOT BE ABLE TO PERFORM HEREUNDER ON AN
                          ECONOMIC BASIS WITHOUT SUCH LIMITATIONS. SOME JURISDICTIONS DO NOT ALLOW THE
                          LIMITATION OF LIABILITY FOR PERSONAL INJURY CAUSED DIRECTLY BY THE NEGLIGENCE
                          OF CARGO SIGNAL, THE LIMITATION OF LIABILITY FOR LOSS OR DAMAGE CAUSED BY THE
                          FRAUDULENT MISREPRESENTATION OF CARGO SIGNAL, OR THE EXCLUSION OF INCIDENTAL
                          OR CONSEQUENTIAL DAMAGES, SO THIS LIMITATION MAY NOT APPLY TO YOU.
                        </p>

                        <p>EXCEPT AS PROHIBITED BY LAW OR PROVIDED IN THE ADDITIONAL TERMS AND</p>
                        <p>
                          CONDITIONS, IN NO EVENT SHALL CARGO SIGNAL&rsquo;S TOTAL LIABILITY TO YOU FOR
                          ANY AND ALL DAMAGES SHALL EXCEED THE GREATER OF (I) TOTAL FEES PAID TO OR
                          EARNED BY CARGO SIGNAL DURING THE ONE (1) MONTH IMMEDIATELY BEFORE THE EVENT
                          GIVING RISE TO THE LIABILITY OR (II) FIFTY U.S. DOLLARS ($50.00). THE
                          FOREGOING LIMITATIONS WILL APPLY EVEN IF THE ABOVE STATED REMEDY FAILS OF ITS
                          ESSENTIAL PURPOSE.
                        </p>

                        <p>
                          The foregoing states the entire obligation of Cargo Signal and your exclusive
                          remedy arising out of this Agreement, your use of the App, Cargo Signal
                          Content, Cargo Signal Intellectual Property, or Cargo Signal Documentation, or
                          Cargo Signal&rsquo;s provision of any Services.
                        </p>
                      </li>
                    </p>
                  </ol>

                  <ol class="list-alpha pl-4" type="A" start="6">
                    <li>
                      <strong> Indemnification:</strong>
                      <p>YOU UNDERSTAND THAT YOU ARE RESPONSIBLE FOR YOUR BEHAVIOR WHILE USING</p>
                      <p>
                        THE APP, FOR THE INFORMATION PROVIDED IN YOU USER ACCOUNT, AND FOR ALL USER
                        CONTENT PROVIDED. ACCORDINGLY, YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD CARGO
                        SIGNAL AND ITS AFFILIATES AND THEIR OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS
                        HARMLESS FROM AND AGAINST ANY LOSS, DAMAGE, LIABILITY, COST, OR EXPENSE OF ANY
                        KIND (INCLUDING ATTORNEYS&rsquo; FEES) THAT CARGO SIGNAL MAY INCUR IN CONNECTION
                        WITH A THIRD-PARTY CLAIM, OR OTHERWISE, IN RELATION TO YOUR USE OF THE APP, THE
                        USER CONTENT YOU CONTRIBUTE OR LINK TO, YOUR ACCESS TO THE APP, THE INFORMATION
                        CONTAINED IN YOUR USER ACCOUNT, OR YOUR VIOLATION OF EITHER THIS AGREEMENT OR
                        THE RIGHTS OF ANY THIRD PARTY. CARGO SIGNAL SHALL HAVE THE RIGHT TO CONTROL AND
                        DIRECT THE DEFENSE OF ANY THIRD PARTY CLAIM OR ACTION PURSUANT TO THIS SECTION,
                        INCLUDING THE RIGHT TO SETTLE THE CLAIM, AND YOU AGREE TO PROVIDE YOUR FULL AND
                        COMPLETE ASSISTANCE AND COOPERATION TO CARGO SIGNAL.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="7">
                    <li>
                      <strong> Global Availability / Export Controls / International Law:</strong>
                      <p>
                        The App, Cargo Signal Content, Cargo Signal Intellectual Property, and Cargo
                        Signal Documentation may be controlled and operated by Cargo Signal from various
                        offices in the United States and internationally and may be accessible by you in
                        the United States and internationally. Cargo Signal makes no representation that
                        the App, Cargo Signal Content, Cargo Signal Intellectual Property, or Cargo
                        Signal Documentation as accessed or described is appropriate or available for
                        use in all locations, and access to them from other countries where the contents
                        are illegal or penalized is prohibited. Access to or use of the
                      </p>
                      <p>
                        App, Cargo Signal Content, Cargo Signal Intellectual Property, or Cargo Signal
                        Documentation by persons or from countries who are sanctioned by the United
                        States is specifically prohibited. Those who access the App, Cargo Signal
                        Content, Cargo Signal Intellectual Property, or Cargo Signal Documentation do so
                        on their own and are responsible for compliance with all applicable United
                        States and local laws, including but not limited to, the U.S. Foreign Corrupt
                        Practices Act (FCPA), the U.S. Export Administration Regulations (EAR), the U.S.
                        Anti-Boycott regulations, and the various U.S. economic sanctions programs
                        administered by the U.S. Treasury Office of Foreign Assets Control (OFAC). If
                        you are not permitted to access the App, Cargo Signal Content, Cargo Signal
                        Intellectual Property, or Cargo Signal Documentation due to any law,
                        legislation, or regulation, you must immediately discontinue use of the App,
                        Cargo Signal Content, Cargo Signal Intellectual Property, and Cargo Signal
                        Documentation.
                      </p>

                      <p>
                        You represent and agree that you will only use the App, Cargo Signal Content, or
                        Cargo Signal
                      </p>
                      <p>
                        Documentation in compliance with all applicable laws, regulations, and
                        legislation of each country where you access the App, Cargo Signal Content,
                        Cargo Signal Intellectual Property, or Cargo Signal Documentation and each
                        country where Cargo Signal provides Services to you. You agree, acknowledge, and
                        understand that you may not export or re-export the App, Cargo Signal Content,
                        Cargo Signal Intellectual Property, Cargo Signal Documentation, or information
                        or materials accessed on or described in the App except in full compliance with
                        all applicable laws, regulations, and legislation. In particular, you may not
                        export or re-export into (or to a national or resident of) any country to which
                        the United States embargoes or sanctions goods, services, or technology, to any
                        person or entity on the United States Treasury Department&rsquo;s List of
                        Specially Designated Nationals, the United States Bureau of Export
                        Administration Entity List, the United States Bureau of Export Administration
                        Denied Persons List, or the United States
                      </p>
                      <p>
                        Commerce Department&rsquo;s Table of Denial Orders. In addition, you are
                        responsible for complying with all applicable laws, regulations, and legislation
                        of any country that relates to, or that in any way affects this Agreement and
                        your performance under this Agreement.
                      </p>

                      <p>
                        IF ANY TERMS OR CONDITIONS OF THIS AGREEMENT ARE FOUND TO BE COUNTER TO ANY
                        APPLICABLE LAW, REGULATION, OR LEGISLATION OF ANY COUNTRY APPLICABLE TO YOUR USE
                        OF THE APP, CARGO SIGNAL CONTENT, OR CARGO SIGNAL DOCUMENTATION AND THEREFORE
                        UNENFORCEABLE UNDER SUCH LAW, REGULATION, OR LEGISLATION, THOSE PARTICULAR TERMS
                        OR CONDITIONS SHALL BE REPLACED BY CARGO SIGNAL TO MAKE THE TERMS OR CONDITIONS
                        COMPLIANT WITH THE APPLICABLE LAW, REGULATION, OR LEGISLATION OF THE APPLICABLE
                        COUNTRY, AND YOU AGREE, ACKNOWLEDGE, AND UNDERSTAND THAT YOU WILL BE BOUND BY
                        THE REPLACED TERMS.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="8">
                    <li>
                      <strong> Security Policy:</strong>
                      <p>
                        Cargo Signal uses commercially reasonable security measures to protect your User
                        Content and your user account information. We have security measures in place to
                        help protect against the unauthorized access of customer information under our
                        control. We utilize both online and offline security methods, including
                        firewalls, passwords, and restricted physical access to the places where your
                        information is stored to help protect the information. Our staff is trained to
                        comply with our security procedures, and our security procedures are regularly
                        reviewed and revised as we deem necessary.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="9">
                    <li>
                      <strong> Additional Terms and Conditions:</strong>
                      <p>
                        In the event of any conflict between these EULA terms and the Additional Terms
                        and Conditions, the Additional Terms and Conditions shall govern and supersede
                        these EULA terms. In addition, nothing contained in these EULA terms shall
                        affect the validity or enforceability of Cargo Signal&rsquo;s terms and
                        conditions of services or trading conditions, which are deemed to govern the
                        relationship between you and Cargo Signal and which are incorporated into this
                        Agreement by reference as if fully stated herein. In the event of any conflict
                        between Cargo Signal&rsquo;s terms and conditions of services or trading
                        conditions and these EULA terms, the terms and conditions of services and
                        trading conditions (as the case may be) shall govern.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="10">
                    <li>
                      <strong> Disputes, Forum, and Governing Law:</strong>
                      <p>
                        Except to the extent prohibited by applicable law, this Agreement, all matters
                        relating to the App, Cargo Signal Content, and Cargo Signal Documentation, and
                        any disputes, claims, suits, causes of action, or proceedings arising out of
                        this Agreement, the App, the Cargo Signal Content, or the Cargo Signal
                        Documentation, including any disputes, claims, suits, or causes of action
                        concerning the interpretation, violation, invalidity, non-performance, or
                        termination of this Agreement, shall be governed by and construed in accordance
                        with the internal laws of the United States of America and the State of
                        Washington, United States of America, without giving effect to any choice or
                        conflict of law provision or rules. If the foregoing choice of law provision is
                        prohibited by applicable law, then this Agreement shall be governed by the laws
                        of the country where the services associated with the App are primarily being
                        provided.
                      </p>

                      <p>
                        Except as prohibited by law or provided below regarding suits we may initiate
                        against you, any legal suit, action, or proceeding arising out of, or related to
                        this Agreement, the App, Cargo Signal Content, or Cargo Signal Documentation
                        will be instituted exclusively in United States District Court for the Western
                        District of Washington, Seattle Division, or the King County Superior Court. We
                        retain the right to bring suit against you for any breach of this Agreement in
                        your country of residence or any other relevant country. You hereby waive any
                        and all objections to the exercise of jurisdiction over you by such courts and
                        to venue in such courts. If the foregoing venue provision is prohibited by
                        applicable law, then this Agreement shall be governed by the laws of the country
                        where the services associated with the App are primarily being provided.
                      </p>

                      <p>
                        In addition, Cargo Signal may, at its sole discretion, or when the foregoing
                        provisions are prohibited by law, require you to submit any disputes, claims,
                        suits, or causes of action relating to this Agreement, the App, Cargo Signal
                        Content, or Cargo Signal Documentation, including any disputes, claims, suits,
                        or causes of action concerning the interpretation, violation, invalidity,
                        non-performance, or termination of this Agreement, to final and binding
                        arbitration under the International Chamber of Commerce (ICC) Rules of
                        Arbitration effective as of March 1, 2017.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="11">
                    <li>
                      <strong> Availability of Injunctive Relief:</strong>
                      <p>
                        You understand and agree that a breach or threatened breach of this Agreement
                        will cause Cargo Signal irreparable damage for which the recovery of money
                        damages alone would be inadequate. Therefore, we will be entitled to obtain
                        injunctive relief to protect and enforce our rights under this Agreement in
                        addition to any and all remedies available at law without the requirement to
                        post a bond (or with the posting of a nominal bond if a bond is required by
                        applicable law).
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="12">
                    <li>
                      <strong> Attorney Fees and Costs:</strong>
                      <p>
                        If either party commences any action or proceeding against the other relating to
                        this Agreement or the App, Cargo Signal Content, Cargo Signal Intellectual
                        Property, or Cargo Signal Documentation, the prevailing party will be entitled
                        to recover from the other party its reasonable attorney fees and costs
                        irrespective of whether the action or proceeding is prosecuted to judgment.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="13">
                    <li>
                      <strong> Controlling Language:</strong>
                      <p>
                        The controlling language of this Agreement is English. If Cargo Signal provided
                        you with a translation of this Agreement into another language, we provided it
                        for your convenience only. This Agreement shall be construed and enforced only
                        in accordance with the English language.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="14">
                    <li>
                      <strong> Miscellaneous:</strong>
                      <p>
                        This Agreement is the complete and entire agreement between you and Cargo Signal
                        pertaining subject matter of this Agreement. It supersedes and replaces any and
                        all prior or contemporaneous agreements, understandings, communications and
                        discussions between you and Cargo Signal relating to this Agreement, the App,
                        your use of the App, the Cargo Signal Content, and the Cargo Signal
                        Documentation. The failure of Cargo Signal to exercise or enforce any right or
                        provision of this Agreement shall not operate as a waiver of such right or
                        provision nor prejudice Cargo Signal&rsquo;s right to take subsequent action.
                        This Agreement operates to the fullest extent permissible by law. If any
                        provision of this Agreement is found unlawful, void, or enforceable by a court
                        based upon any written decree, that provision shall be modified to the extent
                        necessary to render it enforceable without losing its intent or it shall be
                        deemed severable from this Agreement if no such modification is possible. The
                        remainder of this Agreement shall remain in full force and effect and
                        modification or severance of any one provision shall not affect the validity and
                        enforceability of any of the remaining provisions.
                      </p>
                    </li>
                  </ol>
                  <ol class="list-alpha pl-4" type="A" start="15">
                    <li>
                      <strong> How to Contact Us:</strong>
                      <p>
                        If you have any questions or concerns about the Application, you may contact us
                        at:
                      </p>
                      <p>
                        Cargo Signal Solutions, LLC <br />
                        1015 Third Avenue <br />
                        Seattle, Washington 98104 <br />
                        Phone: 206-834-8000 <br />
                        Email:
                        <a href="mailto:commandcenter@cargosignal.com">commandcenter&#64;cargosignal.com</a>
                      </p>
                    </li>
                  </ol>
                </li>
              </ol>
        </div>
        <div class="flex justify-end gap-4">
            <button 
              kendoButton
              class="flex-1 text-base md:max-w-[128px]" 
              (click)="decline()"
            >
              Decline
            </button>
            <button
              kendoButton
              class="flex-1 text-base md:max-w-[128px]"
              themeColor="primary"
              (click)="accept()"
            >
              Accept
            </button>
        </div>
      </div>
    </div>
  `
})
export class EulaComponent implements OnInit {
  private store = inject<Store<fromStore.State>>(Store);
  private authService = inject(AuthService);

  name$: Observable<string> | undefined;

  ngOnInit() {
    this.name$ = this.store
      .select(fromStore.selectUserFullName)
      .pipe(
        filter((user) =>
          user !== undefined
        ),
        distinctUntilChanged(),
      );
  }

  accept(): void {
    this.store.dispatch(fromStore.acceptEULA({ redirectUri: this.authService.redirectUri || '/' }));
  }

  decline(): void {
    this.store.dispatch(fromStore.declineEULA());
  }
}
